import { useEffect, useMemo } from 'react'
import { Link, Outlet, useLocation, useParams } from 'react-router'

import { AppSidebar } from '@/components/AppSidebar'
import { LiveCallsBanner } from '@/components/billing/LiveCallsBanner'
import { IncomingTransfers } from '@/components/calls/IncomingTransfers'
import { CommandMenu, useCommandMenu } from '@/components/CommandMenu'
import { Splash } from '@/components/Splash'
import { Button } from '@/components/ui/button'
import { Empty, EmptyDescription, EmptyHeader } from '@/components/ui/empty'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import type { OrgContext } from '@/hooks/useCurrentOrg'
import { useMe } from '@/hooks/useMe'
import { ShellContext } from '@/hooks/useShell'
import { rememberOrgSlug } from '@/lib/lastOrg'

/**
 * Everything under /o/:orgSlug. Finds the org among the person's orgs (from /api/me), then renders the sidebar and
 * the page. Pages read the org with useCurrentOrg(). A suspended org shows a notice instead of the app.
 */
export const OrgLayout = () => {
  const { orgSlug } = useParams()
  const me = useMe()
  const org = me.data?.orgs.find((candidate) => candidate.slug === orgSlug)
  const commandMenu = useCommandMenu()
  const { setIsOpen: setIsCommandMenuOpen } = commandMenu
  const shell = useMemo(() => ({ openSearch: () => setIsCommandMenuOpen(true) }), [setIsCommandMenuOpen])
  // The section (/o/:orgSlug/calls/… is "calls"): moving between sections fades the page in; within one it doesn't.
  const section = useLocation().pathname.split('/')[3] ?? ''

  useEffect(() => {
    if (org) rememberOrgSlug(org.slug)
  }, [org])

  if (!me.data) return <Splash />
  if (!org)
    return (
      <CenteredNotice
        title="This org isn’t available"
        description="It may have been deleted, or you’re not a member."
      />
    )
  if (org.isSuspended) {
    return (
      <CenteredNotice
        title={`${org.name} is suspended`}
        description="Calls and changes are paused. Contact RowCall support to restore access."
      />
    )
  }

  const context: OrgContext = { org, user: me.data.user, orgs: me.data.orgs }
  return (
    <ShellContext value={shell}>
      <SidebarProvider>
        <AppSidebar org={org} orgs={me.data.orgs} user={me.data.user} />
        <SidebarInset className="min-w-0">
          <div className="mx-auto w-full max-w-7xl px-4 pt-6 pb-16 sm:px-6 lg:px-8">
            <LiveCallsBanner org={org} />
            <div
              key={section}
              className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-1 motion-safe:duration-300 motion-safe:ease-fluid"
            >
              <Outlet context={context} />
            </div>
            <IncomingTransfers org={org} userId={me.data.user.id} />
          </div>
        </SidebarInset>
        <CommandMenu org={org} orgs={me.data.orgs} {...commandMenu} />
      </SidebarProvider>
    </ShellContext>
  )
}

const CenteredNotice = ({ title, description }: { title: string; description: string }) => (
  <div className="flex min-h-svh items-center justify-center p-4">
    <Empty>
      <EmptyHeader>
        <h1 className="text-xl font-medium tracking-tight">{title}</h1>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
      <Button asChild variant="outline">
        <Link to="/">Go to your orgs</Link>
      </Button>
    </Empty>
  </div>
)
